// src/pages/workflow/AdminWorkflowDashboard.js
import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebaseConfig";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { getAuth, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import {
  isNafedProject,
  syncEligibleNafedProjects,
} from "./nafedWorkflowBridge";

/* ----------------- small helpers ----------------- */
const cx = (...xs) => xs.filter(Boolean).join(" ");

function normalizeDistrictName(v = "") {
  const raw = String(v || "").trim();
  const compact = raw.replace(/[\s_-]+/g, "").toLowerCase();

  if (compact === "vavtharad") return "VavTharad";
  if (compact === "banaskantha") return "Banaskantha";
  if (compact === "mahesana" || compact === "mehsana") return "Mahesana";
  if (compact === "patan") return "Patan";
  if (compact === "gandhinagar") return "Gandhinagar";
  if (compact === "ahmedabad") return "Ahmedabad";
  if (compact === "sabarkantha") return "Sabarkantha";
  if (compact === "aravalli") return "Aravalli";
  if (compact === "kutch") return "Kutch";
  if (compact === "surat") return "Surat";
  if (compact === "vadodara") return "Vadodara";
  if (compact === "bhavnagar") return "Bhavnagar";
  if (compact === "rajkot") return "Rajkot";

  return raw;
}

function normalizeProjectRow(row = {}) {
  return {
    ...row,
    district: normalizeDistrictName(row.district || ""),
    taluka: String(row.taluka || "").trim(),
    locationVillage: String(row.locationVillage || "").trim(),
    pacsName: String(row.pacsName || "").trim(),
    title: String(row.title || "").trim(),
  };
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={cx(
        "px-4 py-2 rounded-lg text-sm font-semibold border",
        active
          ? "bg-emerald-600 text-white border-emerald-700"
          : "bg-white text-emerald-700 border-emerald-200",
      )}
    >
      {children}
    </button>
  );
}

function Badge({ color = "gray", children }) {
  const map = {
    green: "bg-green-100 text-green-700",
    yellow: "bg-yellow-100 text-yellow-700",
    red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-700",
    gray: "bg-gray-100 text-gray-700",
    emerald: "bg-emerald-100 text-emerald-700",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
        map[color] || map.gray,
      )}
    >
      {children}
    </span>
  );
}

function StatCard({ label, value, color, icon }) {
  const bg =
    color === "pink"
      ? "bg-pink-600"
      : color === "blue"
        ? "bg-blue-600"
        : color === "purple"
          ? "bg-purple-600"
          : color === "orange"
            ? "bg-orange-600"
            : "bg-slate-900";
  return (
    <div className={cx("rounded-2xl px-5 py-4 text-white shadow-md", bg)}>
      <div className="text-xs uppercase tracking-wide text-white/70">
        {label}
      </div>
      <div className="mt-2 flex items-end justify-between">
        <div className="text-2xl font-extrabold">{value}</div>
        {icon && <div className="text-2xl opacity-70">{icon}</div>}
      </div>
    </div>
  );
}

function ProgressBar({ value }) {
  const pct = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden">
      <div
        className={cx(
          "h-full rounded-full",
          pct < 25
            ? "bg-orange-400"
            : pct < 50
              ? "bg-yellow-400"
              : pct < 75
                ? "bg-sky-500"
                : "bg-emerald-500",
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="text-xs font-semibold text-slate-600">{children}</label>
  );
}

/* -------------------- VENDOR ROLE LIST -------------------- */
const ROLE_OPTIONS = [
  { id: "SURVEY", label: "Survey Engineer / Agency" },
  { id: "SOIL_TEST", label: "Soil Testing Lab" },
  { id: "DESIGN", label: "Structural / Design Engineer" },
  { id: "ESTIMATE", label: "Estimator / QS" },
  { id: "DPR_CA", label: "CA / DPR Consultant" },
];

/* ================= GLOBAL DASHBOARD SYNC HELPERS ================= */
const GLOBAL_PRE_WORK = [
  { code: "MOU", name: "MOU" },
  { code: "SURVEY", name: "Site Survey" },
  { code: "SOIL", name: "Soil Testing" },
  { code: "DESIGN", name: "Design & Structure" },
  { code: "TENDER", name: "Tender Process" },
  { code: "WORK", name: "Work Started" },
];

const GLOBAL_POST_WORK = [
  { code: "EXCAVATION", name: "Excavation / Site Work" },
  { code: "FOOTING", name: "Footing" },
  { code: "PLINTH", name: "Plinth Beam" },
  { code: "COLUMN_BEAM", name: "Column & Beam Work" },
  { code: "BRICK", name: "Brick Masonry Work" },
  { code: "PLASTER", name: "Plaster Work" },
  { code: "PEB_INSPECTION", name: "PEB Inspection" },
  { code: "PEB_INSTALL", name: "PEB Installation" },
  { code: "COLOUR", name: "Colour Work" },
  { code: "LOGO", name: "Logo Design & Branding" },
  { code: "FINISH", name: "Finishing" },
  { code: "DONE", name: "Completed" },
];

const GLOBAL_STAGE_ORDER = [...GLOBAL_PRE_WORK, ...GLOBAL_POST_WORK].map(
  (x) => x.code,
);

const GLOBAL_NAME_BY = Object.fromEntries(
  [...GLOBAL_PRE_WORK, ...GLOBAL_POST_WORK].map((x) => [x.code, x.name]),
);

function globalMergeStages(existing = []) {
  const by = Object.fromEntries((existing || []).map((s) => [s.code, s]));
  return GLOBAL_STAGE_ORDER.map((code) =>
    by[code]
      ? { name: GLOBAL_NAME_BY[code], ...by[code] }
      : { code, name: GLOBAL_NAME_BY[code], status: "Pending" },
  );
}

function globalComputeProgress(stages = []) {
  const total = stages.length || 1;
  const done = stages.filter(
    (s) => String(s.status || "").toLowerCase() === "completed",
  ).length;
  return Math.round((done / total) * 100);
}

function globalComputeCurrentStage(stages = []) {
  const nxt = stages.find(
    (s) => String(s.status || "").toLowerCase() !== "completed",
  );
  return nxt ? nxt.name || nxt.code : "Completed";
}

function mapWorkflowTaskToProjectStage(type = "") {
  const t = String(type || "").toUpperCase();

  if (t === "SURVEY") return "SURVEY";
  if (t === "SOIL_TEST") return "SOIL";
  if (t === "DESIGN") return "DESIGN";
  if (t === "ESTIMATE") return "DESIGN";
  if (t === "DPR_CA") return "DESIGN";

  return "";
}

function mapApprovedTaskToWorkflowStatus(type = "") {
  const t = String(type || "").toUpperCase();

  if (t === "SURVEY") return "SURVEY_DONE";
  if (t === "SOIL_TEST") return "SOIL_DONE";
  if (t === "DESIGN") return "DESIGN_DONE";
  if (t === "ESTIMATE") return "ESTIMATE_DONE";
  if (t === "DPR_CA") return "DPR_DONE";

  return "";
}

function mapRejectedTaskToWorkflowStatus(type = "") {
  const t = String(type || "").toUpperCase();

  if (t === "SURVEY") return "SURVEY_REJECTED";
  if (t === "SOIL_TEST") return "SOIL_REJECTED";
  if (t === "DESIGN") return "DESIGN_REJECTED";
  if (t === "ESTIMATE") return "ESTIMATE_REJECTED";
  if (t === "DPR_CA") return "DPR_REJECTED";

  return "";
}

/* ================= MAIN SHELL ================= */
export default function AdminWorkflowDashboard() {
  const [tab, setTab] = useState("vendors");
  const auth = getAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success("Logged out from Workflow Admin");
      navigate("/workflow/admin-login", { replace: true });
    } catch (e) {
      console.error(e);
      toast.error("Logout failed");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-emerald-950 p-6">
      <Toaster position="top-center" />
      <div className="max-w-6xl mx-auto">
        <header className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-emerald-800">
              Shine Agri · NAFED Workflow Control
            </h1>
            <p className="text-xs md:text-sm text-gray-600">
              Only NAFED projects · allocated PMCs · approved technical vendors
              · stage control
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 active:scale-95"
          >
            Logout
          </button>
        </header>

        <div className="flex gap-2 mb-4">
          <TabBtn active={tab === "vendors"} onClick={() => setTab("vendors")}>
            Work Vendors
          </TabBtn>
          <TabBtn
            active={tab === "projects"}
            onClick={() => setTab("projects")}
          >
            Projects &amp; MOU
          </TabBtn>
          <TabBtn active={tab === "tasks"} onClick={() => setTab("tasks")}>
            Tasks (All)
          </TabBtn>
        </div>

        <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-5">
          {tab === "vendors" && <VendorsPane />}
          {tab === "projects" && <ProjectsPane />}
          {tab === "tasks" && <TasksPane />}
        </div>
      </div>
    </div>
  );
}

/* ================= WORK VENDORS PANE ================= */
function VendorsPane() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, "work_vendors"));
    const list = [];
    snap.forEach((d) => {
      const row = { id: d.id, ...d.data() };
      if (row.isDeleted !== true && row.controlledByPortal === "SHINE_AGRI")
        list.push(row);
    });
    setRows(list);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((v) => {
      const hay = [
        v.name,
        v.firmName,
        v.email,
        v.phone,
        (v.categories || []).join(","),
      ]
        .map((s) => String(s || "").toLowerCase())
        .join("|");
      return hay.includes(needle);
    });
  }, [rows, q]);

  const toggleCategory = async (v, catId, checked) => {
    const current = v.approvedCategories || [];
    const next = checked
      ? Array.from(new Set([...current, catId]))
      : current.filter((x) => x !== catId);

    await updateDoc(doc(db, "work_vendors", v.id), {
      approvedCategories: next,
      updatedAt: serverTimestamp(),
    });
    toast.success("Approved categories updated");
    load();
  };

  const setStatus = async (v, status) => {
    await updateDoc(doc(db, "work_vendors", v.id), {
      status,
      isActive: status === "approved",
      visibleToApprovedNafedPmcs: status === "approved",
      approvedByPortal: status === "approved" ? "SHINE_AGRI" : "",
      updatedAt: serverTimestamp(),
    });
    toast.success(`Status updated to ${status}`);
    load();
  };

  const autoApproveAll = async (v) => {
    await updateDoc(doc(db, "work_vendors", v.id), {
      status: "approved",
      approvedCategories: v.categories || [],
      isActive: true,
      visibleToApprovedNafedPmcs: true,
      approvedByPortal: "SHINE_AGRI",
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    toast.success("Vendor approved with all requested categories");
    load();
  };

  const statusColor = (status) =>
    status === "approved" ? "green" : status === "blocked" ? "red" : "yellow";

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-emerald-800">
            Work Vendors (Survey / Soil / Design / Estimate / DPR)
          </span>
          <span className="text-xs text-gray-500">
            Approve categories → vendor portal me role choose karega → sirf apne
            hi tasks dekh payega.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div>
            <Label>Search</Label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name / Firm / Email"
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <button
            onClick={load}
            className="mt-5 px-3 py-2 rounded-lg text-xs border bg-emerald-50 hover:bg-emerald-100"
          >
            Reload
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading vendors…</div>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="min-w-full text-xs border border-black"
            style={{ borderCollapse: "collapse" }}
          >
            <thead className="bg-emerald-50">
              <tr>
                <th className="px-2 py-2 border border-black text-left">#</th>
                <th className="px-2 py-2 border border-black text-left">
                  Vendor
                </th>
                <th className="px-2 py-2 border border-black text-left">
                  Email / Phone
                </th>
                <th className="px-2 py-2 border border-black text-left">
                  Requested Categories
                </th>
                <th className="px-2 py-2 border border-black text-left">
                  Approved Categories
                </th>
                <th className="px-2 py-2 border border-black text-left">
                  Status
                </th>
                <th className="px-2 py-2 border border-black text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v, idx) => {
                const req = v.categories || [];
                const appr = v.approvedCategories || [];
                return (
                  <tr
                    key={v.id}
                    className="border border-black hover:bg-emerald-50"
                  >
                    <td className="px-2 py-1 border border-black">{idx + 1}</td>
                    <td className="px-2 py-1 border border-black">
                      <div className="font-semibold text-gray-900">
                        {v.name}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {v.firmName}
                      </div>
                    </td>
                    <td className="px-2 py-1 border border-black">
                      <div>{v.email}</div>
                      <div className="text-[11px] text-gray-500">{v.phone}</div>
                    </td>
                    <td className="px-2 py-1 border border-black">
                      <div className="flex flex-col gap-1">
                        {ROLE_OPTIONS.map((r) =>
                          req.includes(r.id) ? (
                            <span key={r.id} className="text-[11px]">
                              • {r.label}
                            </span>
                          ) : null,
                        )}
                        {!req.length && (
                          <span className="text-[11px] text-gray-400">
                            No category selected at registration
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1 border border-black align-top">
                      <div className="flex flex-col gap-1">
                        {ROLE_OPTIONS.map((r) => (
                          <label
                            key={r.id}
                            className="flex items-center gap-1 text-[11px]"
                          >
                            <input
                              type="checkbox"
                              checked={appr.includes(r.id)}
                              onChange={(e) =>
                                toggleCategory(v, r.id, e.target.checked)
                              }
                              className="w-3 h-3"
                            />
                            <span>{r.label}</span>
                          </label>
                        ))}
                      </div>
                    </td>
                    <td className="px-2 py-1 border border-black">
                      <Badge color={statusColor(v.status || "pending")}>
                        {v.status || "pending"}
                      </Badge>
                    </td>
                    <td className="px-2 py-1 border border-black">
                      <div className="flex flex-col gap-1 items-stretch">
                        <button
                          onClick={() => autoApproveAll(v)}
                          className="px-2 py-1 rounded bg-emerald-600 text-white text-[11px] hover:bg-emerald-700"
                        >
                          Approve + All Cats
                        </button>
                        <button
                          onClick={() => setStatus(v, "approved")}
                          className="px-2 py-1 rounded border text-[11px] hover:bg-emerald-50"
                        >
                          Mark Approved
                        </button>
                        <button
                          onClick={() => setStatus(v, "blocked")}
                          className="px-2 py-1 rounded bg-red-600 text-white text-[11px] hover:bg-red-700"
                        >
                          Block
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center text-gray-500 py-6 border border-black"
                  >
                    No vendors found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ================= PROJECTS & MOU PANE ================= */
function ProjectsPane() {
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    title: "",
    pacsName: "",
    pacsContactName: "",
    pacsContactPhone: "",
    locationVillage: "",
    taluka: "",
    district: "",
    state: "Gujarat",
    googleMapsUrl: "",
    mouDate: "",
    status: "NEW",
    surveyVendorId: "",
    soilVendorId: "",
    designVendorId: "",
    estimateVendorId: "",
    dprVendorId: "",
  });

  /* ================= PROJECT DASHBOARD SYNC HELPERS ================= */

  const PRE_WORK = [
    { code: "MOU", name: "MOU" },
    { code: "SURVEY", name: "Site Survey" },
    { code: "SOIL", name: "Soil Testing" },
    { code: "DESIGN", name: "Design & Structure" },
    { code: "TENDER", name: "Tender Process" },
    { code: "WORK", name: "Work Started" },
  ];

  const POST_WORK = [
    { code: "EXCAVATION", name: "Excavation / Site Work" },
    { code: "FOOTING", name: "Footing" },
    { code: "PLINTH", name: "Plinth Beam" },
    { code: "COLUMN_BEAM", name: "Column & Beam Work" },
    { code: "BRICK", name: "Brick Masonry Work" },
    { code: "PLASTER", name: "Plaster Work" },
    { code: "PEB_INSPECTION", name: "PEB Inspection" },
    { code: "PEB_INSTALL", name: "PEB Installation" },
    { code: "COLOUR", name: "Colour Work" },
    { code: "LOGO", name: "Logo Design & Branding" },
    { code: "FINISH", name: "Finishing" },
    { code: "DONE", name: "Completed" },
  ];

  const STAGE_ORDER = [...PRE_WORK, ...POST_WORK].map((x) => x.code);
  const NAME_BY = Object.fromEntries(
    [...PRE_WORK, ...POST_WORK].map((x) => [x.code, x.name]),
  );

  function mergeStages(existing = []) {
    const by = Object.fromEntries(existing.map((s) => [s.code, s]));
    return STAGE_ORDER.map((code) =>
      by[code]
        ? { name: NAME_BY[code], ...by[code] }
        : { code, name: NAME_BY[code], status: "Pending" },
    );
  }

  function computeProgress(stages = []) {
    const total = stages.length || 1;
    const done = stages.filter(
      (s) => String(s.status || "").toLowerCase() === "completed",
    ).length;
    return Math.round((done / total) * 100);
  }

  function computeCurrentStage(stages = []) {
    const nxt = stages.find(
      (s) => String(s.status || "").toLowerCase() !== "completed",
    );
    return nxt ? nxt.name || nxt.code : "Completed";
  }

  const buildProjectsDocPayload = (
    wfPayload,
    workflowProjectId,
    oldData = null,
  ) => {
    const stages = oldData?.stages || mergeStages([]);
    const progressPercent = oldData?.progressPercent ?? computeProgress(stages);
    const currentStage = oldData?.currentStage || computeCurrentStage(stages);

    return {
      name: wfPayload.title || oldData?.name || "",
      pacsName: wfPayload.pacsName || oldData?.pacsName || "",
      district: normalizeDistrictName(
        wfPayload.district || oldData?.district || "",
      ),
      owner: wfPayload.pacsContactName || oldData?.owner || "",
      notes: [
        String(wfPayload.locationVillage || "").trim(),
        String(wfPayload.taluka || "").trim(),
        wfPayload.state || "",
        wfPayload.pacsContactPhone || "",
        wfPayload.googleMapsUrl || "",
        wfPayload.mouDate ? `MOU Date: ${wfPayload.mouDate}` : "",
        wfPayload.status ? `Workflow Status: ${wfPayload.status}` : "",
      ]
        .filter(Boolean)
        .join(" • "),
      workflowProjectId,
      source: "WORKFLOW_ADMIN",
      projectSource: "WORKFLOW_ADMIN",
      stakeholderVisible: true,
      syncStatus: "ACTIVE",
      workflowSyncedAt: serverTimestamp(),
      workflowStatus: wfPayload.status || "",
      workflowProjectTitle: wfPayload.title || "",
      locationVillage: String(wfPayload.locationVillage || "").trim(),
      taluka: String(wfPayload.taluka || "").trim(),
      state: wfPayload.state || "",
      pacsContactPhone: wfPayload.pacsContactPhone || "",
      googleMapsUrl: wfPayload.googleMapsUrl || "",
      mouDate: wfPayload.mouDate || "",
      stages,
      progressPercent,
      currentStage,
      lastUpdated: serverTimestamp(),
      projectId: oldData?.projectId || `PRJ-${Date.now().toString().slice(-6)}`,
    };
  };

  const syncToProjectsDashboard = async (workflowProjectId, wfPayload) => {
    const qSnap = await getDocs(
      query(
        collection(db, "projects"),
        where("workflowProjectId", "==", workflowProjectId),
      ),
    );

    if (qSnap.empty) {
      const mapped = buildProjectsDocPayload(
        wfPayload,
        workflowProjectId,
        null,
      );
      await addDoc(collection(db, "projects"), {
        ...mapped,
        createdAt: serverTimestamp(),
      });
    } else {
      const existingDoc = qSnap.docs[0];
      const existingData = existingDoc.data();
      const mapped = buildProjectsDocPayload(
        wfPayload,
        workflowProjectId,
        existingData,
      );

      await updateDoc(doc(db, "projects", existingDoc.id), mapped);
    }
  };

  const deleteFromProjectsDashboard = async (workflowProjectId) => {
    const qSnap = await getDocs(
      query(
        collection(db, "projects"),
        where("workflowProjectId", "==", workflowProjectId),
      ),
    );

    for (const d of qSnap.docs) {
      await deleteDoc(doc(db, "projects", d.id));
    }
  };

  /* ================= END SYNC HELPERS ================= */

  const load = async () => {
    setLoading(true);
    await syncEligibleNafedProjects(db);
    const [pSnap, vSnap] = await Promise.all([
      getDocs(collection(db, "wf_projects")),
      getDocs(collection(db, "work_vendors")),
    ]);

    const pList = [];
    pSnap.forEach((d) => {
      const row = normalizeProjectRow({ id: d.id, ...d.data() });
      if (isNafedProject(row) && row.syncStatus !== "DELETED") pList.push(row);
    });
    pList.sort(
      (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0),
    );
    setProjects(pList);

    const vList = [];
    vSnap.forEach((d) => vList.push({ id: d.id, ...d.data() }));
    setVendors(
      vList.filter(
        (v) =>
          v.status === "approved" &&
          v.isActive !== false &&
          v.isDeleted !== true &&
          v.controlledByPortal === "SHINE_AGRI",
      ),
    );

    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      title: "",
      pacsName: "",
      pacsContactName: "",
      pacsContactPhone: "",
      locationVillage: "",
      taluka: "",
      district: "",
      state: "Gujarat",
      googleMapsUrl: "",
      mouDate: "",
      status: "NEW",
      surveyVendorId: "",
      soilVendorId: "",
      designVendorId: "",
      estimateVendorId: "",
      dprVendorId: "",
    });
  };

  const handleSave = async () => {
    if (!form.title || !form.pacsName || !form.mouDate) {
      toast.error("Please fill Project Title, PACS Name & MOU Date");
      return;
    }
    try {
      const normalizedForm = {
        ...form,
        title: String(form.title || "").trim(),
        pacsName: String(form.pacsName || "").trim(),
        pacsContactName: String(form.pacsContactName || "").trim(),
        pacsContactPhone: String(form.pacsContactPhone || "").trim(),
        locationVillage: String(form.locationVillage || "").trim(),
        taluka: String(form.taluka || "").trim(),
        district: normalizeDistrictName(form.district || ""),
        state: String(form.state || "").trim() || "Gujarat",
        googleMapsUrl: String(form.googleMapsUrl || "").trim(),
        nafedProject: true,
        projectSource: "NAFED",
        applicationPortal: "NAFED_PACS_APPLY",
      };

      if (editingId) {
        // 🔹 Existing project update
        await updateDoc(doc(db, "wf_projects", editingId), {
          ...normalizedForm,
          updatedAt: serverTimestamp(),
        });

        // 🔹 Sync to main Projects Dashboard collection
        await syncToProjectsDashboard(editingId, normalizedForm);

        toast.success("Project updated");

        // 🔹 Agar status MOU_COMPLETED hai to yahan bhi initial tasks ensure karo
        if (normalizedForm.status === "MOU_COMPLETED") {
          await createInitialTasks(editingId, normalizedForm);
        }
      } else {
        // 🔹 New project create
        const ref = await addDoc(collection(db, "wf_projects"), {
          ...normalizedForm,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        // 🔹 Sync to main Projects Dashboard collection
        await syncToProjectsDashboard(ref.id, normalizedForm);

        toast.success("Project created");

        // 🔹 Naye project ke liye MOU_COMPLETED par tasks banao
        if (normalizedForm.status === "MOU_COMPLETED") {
          await createInitialTasks(ref.id, normalizedForm);
        }
      }
      await load();
      resetForm();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Save failed");
    }
  };

  const createInitialTasks = async (projectId, f) => {
    // ✅ Pehle se koi SURVEY / SOIL_TEST task hai kya? – duplicates avoid karne ke liye
    const existingSnap = await getDocs(
      query(
        collection(db, "wf_project_tasks"),
        where("projectId", "==", projectId),
      ),
    );
    const existingTypes = new Set();
    existingSnap.forEach((d) => {
      const t = d.data();
      if (t.type) existingTypes.add(String(t.type).toUpperCase());
    });

    const now = serverTimestamp();

    // SURVEY task
    if (f.surveyVendorId && !existingTypes.has("SURVEY")) {
      await addDoc(collection(db, "wf_project_tasks"), {
        projectId,
        type: "SURVEY",
        vendorId: f.surveyVendorId,
        status: "ASSIGNED",
        assignedAt: now,
        createdAt: now,
        nafedProject: true,
        allocatedPmcUid: f.allocatedPmcUid || "",
      });
    }

    // SOIL_TEST task
    if (f.soilVendorId && !existingTypes.has("SOIL_TEST")) {
      await addDoc(collection(db, "wf_project_tasks"), {
        projectId,
        type: "SOIL_TEST",
        vendorId: f.soilVendorId,
        status: "ASSIGNED",
        assignedAt: now,
        createdAt: now,
        nafedProject: true,
        allocatedPmcUid: f.allocatedPmcUid || "",
      });
    }

    // Agar dono me se kam se kam ek naya bana hai tabhi toast dikhao
    if (
      (f.surveyVendorId && !existingTypes.has("SURVEY")) ||
      (f.soilVendorId && !existingTypes.has("SOIL_TEST"))
    ) {
      toast.success("Initial tasks (Survey / Soil) created");
    }
  };

  const editProject = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title || "",
      pacsName: p.pacsName || "",
      pacsContactName: p.pacsContactName || "",
      pacsContactPhone: p.pacsContactPhone || "",
      locationVillage: p.locationVillage || "",
      taluka: p.taluka || "",
      district: normalizeDistrictName(p.district || ""),
      state: p.state || "Gujarat",
      googleMapsUrl: p.googleMapsUrl || "",
      mouDate: p.mouDate || "",
      status: p.status || "NEW",
      surveyVendorId: p.surveyVendorId || "",
      soilVendorId: p.soilVendorId || "",
      designVendorId: p.designVendorId || "",
      estimateVendorId: p.estimateVendorId || "",
      dprVendorId: p.dprVendorId || "",
    });
    toast("Edit mode – update & Save Project");
  };

  const removeProject = async (p) => {
    const ok = window.confirm(
      `Delete project "${p.title || p.pacsName || p.id}"?\n\n` +
        "Iss project ke saare Survey / Soil / Design / Estimate / DPR tasks bhi delete ho jayenge.",
    );
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "wf_projects", p.id));

      const tSnap = await getDocs(
        query(
          collection(db, "wf_project_tasks"),
          where("projectId", "==", p.id),
        ),
      );
      for (const d of tSnap.docs) {
        await deleteDoc(d.ref);
      }

      // 🔹 Also delete linked project from Projects Dashboard collection
      await deleteFromProjectsDashboard(p.id);

      toast.success("Project & all related tasks deleted");
      load();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Delete failed");
    }
  };

  const vendorOptions = (roleId) =>
    vendors
      .filter((v) => (v.approvedCategories || []).includes(roleId))
      .map((v) => ({
        value: v.id,
        label: `${v.name} (${v.firmName || "—"})`,
      }));

  const labelForStatus = (s) => s.replace(/_/g, " ");
  const statusColor = (s) => {
    if (s?.endsWith("DONE")) return "green";
    if (s?.endsWith("IN_PROGRESS")) return "yellow";
    if (s === "MOU_COMPLETED") return "blue";
    return "gray";
  };

  return (
    <div className="space-y-5">
      {/* form */}
      <div className="border border-emerald-100 rounded-2xl p-4 bg-emerald-50/60">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-emerald-800">
              {editingId ? "Edit Project / MOU" : "Create New Project / MOU"}
            </div>
            <div className="text-[11px] text-gray-500">
              PACS details + location (Google map link) + role-wise vendor
              selection
            </div>
          </div>
          <button
            onClick={resetForm}
            className="px-3 py-1.5 rounded-lg border text-xs hover:bg-emerald-100"
          >
            Reset
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Project Title</Label>
            <input
              value={form.title}
              onChange={(e) =>
                setForm((f) => ({ ...f, title: e.target.value }))
              }
              placeholder="e.g., Dabhoda PACS – 1000 MT Godown"
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div>
            <Label>PACS / Society Name</Label>
            <input
              value={form.pacsName}
              onChange={(e) =>
                setForm((f) => ({ ...f, pacsName: e.target.value }))
              }
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-3">
          <div>
            <Label>PACS Contact Person</Label>
            <input
              value={form.pacsContactName}
              onChange={(e) =>
                setForm((f) => ({ ...f, pacsContactName: e.target.value }))
              }
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div>
            <Label>PACS Contact Mobile</Label>
            <input
              value={form.pacsContactPhone}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  pacsContactPhone: e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 10),
                }))
              }
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
              placeholder="10-digit mobile"
            />
          </div>
          <div>
            <Label>MOU Date</Label>
            <input
              type="date"
              value={form.mouDate}
              onChange={(e) =>
                setForm((f) => ({ ...f, mouDate: e.target.value }))
              }
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-4 mt-3">
          <div>
            <Label>Village / Place</Label>
            <input
              value={form.locationVillage}
              onChange={(e) =>
                setForm((f) => ({ ...f, locationVillage: e.target.value }))
              }
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div>
            <Label>Taluka</Label>
            <input
              value={form.taluka}
              onChange={(e) =>
                setForm((f) => ({ ...f, taluka: e.target.value }))
              }
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div>
            <Label>District</Label>
            <input
              value={form.district}
              onChange={(e) =>
                setForm((f) => ({ ...f, district: e.target.value }))
              }
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div>
            <Label>State</Label>
            <input
              value={form.state}
              onChange={(e) =>
                setForm((f) => ({ ...f, state: e.target.value }))
              }
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
        </div>

        <div className="mt-3">
          <Label>Google Maps Location URL</Label>
          <input
            value={form.googleMapsUrl}
            onChange={(e) =>
              setForm((f) => ({ ...f, googleMapsUrl: e.target.value }))
            }
            placeholder="Paste Google Maps share link here"
            className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Ye link Survey / Soil / Design vendor ko unke portal me direct
            dikhaya jayega.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <div>
            <Label>Status</Label>
            <select
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value }))
              }
            >
              {[
                "NEW",
                "MOU_COMPLETED",
                "SURVEY_IN_PROGRESS",
                "SURVEY_DONE",
                "SOIL_IN_PROGRESS",
                "SOIL_DONE",
                "DESIGN_IN_PROGRESS",
                "DESIGN_DONE",
                "ESTIMATE_IN_PROGRESS",
                "ESTIMATE_DONE",
                "DPR_IN_PROGRESS",
                "DPR_DONE",
              ].map((s) => (
                <option key={s} value={s}>
                  {labelForStatus(s)}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-gray-500 mt-1">
              MOU complete hote hi "MOU_COMPLETED" select karein. Isse Survey /
              Soil ke tasks generate honge.
            </p>
          </div>
          <div>
            <Label>Survey Vendor</Label>
            <select
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
              value={form.surveyVendorId}
              onChange={(e) =>
                setForm((f) => ({ ...f, surveyVendorId: e.target.value }))
              }
            >
              <option value="">— Select —</option>
              {vendorOptions("SURVEY").map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Soil Testing Vendor</Label>
            <select
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
              value={form.soilVendorId}
              onChange={(e) =>
                setForm((f) => ({ ...f, soilVendorId: e.target.value }))
              }
            >
              <option value="">— Select —</option>
              {vendorOptions("SOIL_TEST").map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-3">
          <div>
            <Label>Design Vendor</Label>
            <select
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
              value={form.designVendorId}
              onChange={(e) =>
                setForm((f) => ({ ...f, designVendorId: e.target.value }))
              }
            >
              <option value="">— Select —</option>
              {vendorOptions("DESIGN").map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Estimate Vendor</Label>
            <select
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
              value={form.estimateVendorId}
              onChange={(e) =>
                setForm((f) => ({ ...f, estimateVendorId: e.target.value }))
              }
            >
              <option value="">— Select —</option>
              {vendorOptions("ESTIMATE").map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>DPR / CA Vendor</Label>
            <select
              className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
              value={form.dprVendorId}
              onChange={(e) =>
                setForm((f) => ({ ...f, dprVendorId: e.target.value }))
              }
            >
              <option value="">— Select —</option>
              {vendorOptions("DPR_CA").map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={resetForm}
            className="px-4 py-2 rounded-lg border text-sm hover:bg-emerald-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            {editingId ? "Save Project" : "Create Project"}
          </button>
        </div>
      </div>

      {/* list */}
      <div className="border border-emerald-100 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-emerald-800">
            All Projects
          </div>
          <button
            onClick={load}
            className="px-3 py-1.5 rounded-lg border text-xs hover:bg-emerald-50"
          >
            Reload
          </button>
        </div>

        {loading ? (
          <div className="text-xs text-gray-500">Loading projects…</div>
        ) : (
          <div className="overflow-x-auto">
            <table
              className="min-w-full text-xs border border-black"
              style={{ borderCollapse: "collapse" }}
            >
              <thead className="bg-emerald-50">
                <tr>
                  <th className="px-2 py-2 border border-black text-left">
                    Project
                  </th>
                  <th className="px-2 py-2 border border-black text-left">
                    PACS / Contact
                  </th>
                  <th className="px-2 py-2 border border-black text-left">
                    Location
                  </th>
                  <th className="px-2 py-2 border border-black text-left">
                    Survey / Soil
                  </th>
                  <th className="px-2 py-2 border border-black text-left">
                    Design / Est / DPR
                  </th>
                  <th className="px-2 py-2 border border-black text-left">
                    Status
                  </th>
                  <th className="px-2 py-2 border border-black text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => {
                  const surveyVendor = vendors.find(
                    (v) => v.id === p.surveyVendorId,
                  );
                  const soilVendor = vendors.find(
                    (v) => v.id === p.soilVendorId,
                  );
                  const designVendor = vendors.find(
                    (v) => v.id === p.designVendorId,
                  );
                  const estVendor = vendors.find(
                    (v) => v.id === p.estimateVendorId,
                  );
                  const dprVendor = vendors.find((v) => v.id === p.dprVendorId);

                  return (
                    <tr
                      key={p.id}
                      className="border border-black hover:bg-emerald-50"
                    >
                      <td className="px-2 py-1 border border-black align-top">
                        <div className="font-semibold text-gray-900">
                          {p.title}
                        </div>
                        <div className="text-[11px] text-gray-500">
                          MOU: {p.mouDate || "—"}
                        </div>
                      </td>
                      <td className="px-2 py-1 border border-black align-top">
                        <div className="text-[11px]">{p.pacsName || "—"}</div>
                        <div className="text-[11px] text-gray-500">
                          {p.pacsContactName || "—"} (
                          {p.pacsContactPhone || "—"})
                        </div>
                      </td>
                      <td className="px-2 py-1 border border-black align-top">
                        <div className="text-[11px]">
                          {p.locationVillage}, {p.taluka}, {p.district},{" "}
                          {p.state}
                        </div>
                        {p.googleMapsUrl && (
                          <a
                            href={p.googleMapsUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[11px] text-emerald-700 underline"
                          >
                            Open in Google Maps
                          </a>
                        )}
                      </td>
                      <td className="px-2 py-1 border border-black align-top">
                        <div className="text-[11px]">
                          <b>Survey:</b>{" "}
                          {surveyVendor ? surveyVendor.name : "—"}
                        </div>
                        <div className="text-[11px]">
                          <b>Soil:</b> {soilVendor ? soilVendor.name : "—"}
                        </div>
                      </td>
                      <td className="px-2 py-1 border border-black align-top">
                        <div className="text-[11px]">
                          <b>Design:</b>{" "}
                          {designVendor ? designVendor.name : "—"}
                        </div>
                        <div className="text-[11px]">
                          <b>Est.:</b> {estVendor ? estVendor.name : "—"}
                        </div>
                        <div className="text-[11px]">
                          <b>DPR:</b> {dprVendor ? dprVendor.name : "—"}
                        </div>
                      </td>
                      <td className="px-2 py-1 border border-black align-top">
                        <Badge color={statusColor(p.status || "NEW")}>
                          {labelForStatus(p.status || "NEW")}
                        </Badge>
                      </td>
                      <td className="px-2 py-1 border border-black align-top text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => editProject(p)}
                            className="px-3 py-1 rounded-lg border text-[11px] hover:bg-emerald-50"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => removeProject(p)}
                            className="px-3 py-1 rounded-lg bg-red-600 text-white text-[11px] hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!projects.length && (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center text-gray-500 py-6 border border-black"
                    >
                      No projects found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= TASKS PANE – DASHBOARD + DETAIL ================= */
function TasksPane() {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  const [districtFilter, setDistrictFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [queryText, setQueryText] = useState("");

  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const load = async () => {
    setLoading(true);
    const [tSnap, pSnap, vSnap] = await Promise.all([
      getDocs(collection(db, "wf_project_tasks")),
      getDocs(collection(db, "wf_projects")),
      getDocs(collection(db, "work_vendors")),
    ]);

    const tList = [];
    tSnap.forEach((d) => {
      const row = { id: d.id, ...d.data() };
      if (row.nafedProject === true && row.isDeleted !== true) tList.push(row);
    });
    tList.sort(
      (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0),
    );
    setTasks(tList);

    const pList = [];
    pSnap.forEach((d) => {
      const row = normalizeProjectRow({ id: d.id, ...d.data() });
      if (isNafedProject(row) && row.syncStatus !== "DELETED") pList.push(row);
    });
    setProjects(pList);

    const vList = [];
    vSnap.forEach((d) => {
      const row = { id: d.id, ...d.data() };
      if (row.controlledByPortal === "SHINE_AGRI" && row.isDeleted !== true)
        vList.push(row);
    });
    setVendors(vList);

    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const labelType = (t) => {
    if (t === "SURVEY") return "Survey";
    if (t === "SOIL_TEST") return "Soil Test";
    if (t === "DESIGN") return "Design";
    if (t === "ESTIMATE") return "Estimate";
    if (t === "DPR_CA") return "DPR / CA";
    return t;
  };

  const statusColor = (s) => {
    if (s === "COMPLETED") return "green";
    if (s === "ASSIGNED") return "yellow";
    if (s === "ACCEPTED" || s === "IN_PROGRESS") return "blue";
    return "gray";
  };

  /* per-project views */
  const projectViews = useMemo(() => {
    const groupTasks = {};
    for (const t of tasks) {
      if (!t.projectId) continue;
      if (!groupTasks[t.projectId]) groupTasks[t.projectId] = [];
      groupTasks[t.projectId].push(t);
    }

    return projects.map((p) => {
      const pTasks = groupTasks[p.id] || [];
      const total = pTasks.length;
      const completed = pTasks.filter(
        (t) => (t.status || "").toUpperCase() === "COMPLETED",
      ).length;
      const progress = total === 0 ? 0 : Math.round((completed / total) * 100);

      let stage = "Not Started";
      if (total === 0 || progress === 0) stage = "Not Started";
      else if (progress === 100) stage = "Completed";
      else if (progress >= 75) stage = "Near Completion";
      else stage = "In Progress";

      const lastUpdated =
        pTasks
          .map((t) => t.updatedAt || t.completedAt || t.assignedAt)
          .filter(Boolean)
          .sort((a, b) => (b.seconds || 0) - (a.seconds || 0))[0] || null;

      return {
        ...p,
        _tasks: pTasks,
        progress,
        stage,
        lastUpdated,
      };
    });
  }, [projects, tasks]);

  const totals = useMemo(() => {
    const total = projectViews.length;
    const notStarted = projectViews.filter(
      (p) => p.stage === "Not Started",
    ).length;
    const completed = projectViews.filter(
      (p) => p.stage === "Completed",
    ).length;
    const nearCompletion = projectViews.filter(
      (p) => p.stage === "Near Completion",
    ).length;
    const inProgress = total - notStarted - completed - nearCompletion;
    return { total, notStarted, inProgress, nearCompletion, completed };
  }, [projectViews]);

  const districtOptions = useMemo(() => {
    const set = new Set(
      projectViews.map((p) => (p.district || "").trim()).filter(Boolean),
    );
    return ["all", ...Array.from(set)];
  }, [projectViews]);

  const stageOptions = [
    "all",
    "Not Started",
    "In Progress",
    "Near Completion",
    "Completed",
  ];

  const filteredViews = useMemo(() => {
    let list = [...projectViews];
    if (districtFilter !== "all") {
      list = list.filter((p) => (p.district || "") === districtFilter);
    }
    if (stageFilter !== "all") {
      list = list.filter((p) => p.stage === stageFilter);
    }
    const q = queryText.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const hay = [
          p.pacsName,
          p.title,
          p.locationVillage,
          p.taluka,
          p.district,
          p.id,
        ]
          .map((x) => String(x || "").toLowerCase())
          .join("|");
        return hay.includes(q);
      });
    }
    list.sort((a, b) => {
      const dA = String(a.district || "");
      const dB = String(b.district || "");
      if (dA !== dB) return dA.localeCompare(dB);
      return (b.progress || 0) - (a.progress || 0);
    });
    return list;
  }, [projectViews, districtFilter, stageFilter, queryText]);

  const districtSummary = useMemo(() => {
    const map = new Map();
    for (const p of filteredViews) {
      const key = p.district || "Unknown";
      map.set(key, (map.get(key) || 0) + 1);
    }
    return Array.from(map.entries());
  }, [filteredViews]);

  const progressBuckets = useMemo(() => {
    const buckets = {
      "0–25%": 0,
      "25–50%": 0,
      "50–75%": 0,
      "75–100%": 0,
    };
    for (const p of filteredViews) {
      const val = p.progress || 0;
      if (val < 25) buckets["0–25%"]++;
      else if (val < 50) buckets["25–50%"]++;
      else if (val < 75) buckets["50–75%"]++;
      else buckets["75–100%"]++;
    }
    return buckets;
  }, [filteredViews]);

  const syncTaskDecisionToLinkedProject = async (
    task,
    decision,
    reason = "",
  ) => {
    try {
      if (!task?.projectId) return;

      const linkedSnap = await getDocs(
        query(
          collection(db, "projects"),
          where("workflowProjectId", "==", task.projectId),
        ),
      );

      const projectStageCode = mapWorkflowTaskToProjectStage(task.type);
      const nowTs = Timestamp.now();
      const nextWorkflowStatus =
        decision === "approved"
          ? mapApprovedTaskToWorkflowStatus(task.type)
          : mapRejectedTaskToWorkflowStatus(task.type);

      if (nextWorkflowStatus) {
        await updateDoc(doc(db, "wf_projects", task.projectId), {
          status: nextWorkflowStatus,
          lastWorkflowTaskType: task.type || "",
          lastWorkflowDecision: decision,
          lastWorkflowDecisionReason: reason || "",
          updatedAt: serverTimestamp(),
        });
      }

      if (linkedSnap.empty || !projectStageCode) return;

      for (const linkedDoc of linkedSnap.docs) {
        const oldData = linkedDoc.data() || {};
        const stages = globalMergeStages(oldData.stages || []).map((s) => {
          if (s.code !== projectStageCode) return s;

          if (decision === "approved") {
            return {
              ...s,
              status: "Completed",
              completedAt: nowTs,
              rejectedAt: null,
              lastStageUpdatedAt: nowTs,
              lastStageNote:
                reason ||
                `${task.type || "Workflow task"} approved from Workflow Admin`,
            };
          }

          return {
            ...s,
            status: "Rejected",
            rejectedAt: nowTs,
            lastStageUpdatedAt: nowTs,
            lastStageNote:
              reason ||
              `${task.type || "Workflow task"} rejected from Workflow Admin`,
          };
        });

        const progressPercent = globalComputeProgress(stages);
        const currentStage = globalComputeCurrentStage(stages);

        await updateDoc(doc(db, "projects", linkedDoc.id), {
          stages,
          progressPercent,
          currentStage,
          workflowStatus: nextWorkflowStatus || oldData.workflowStatus || "",
          lastWorkflowTaskType: task.type || "",
          lastWorkflowDecision: decision,
          lastWorkflowDecisionReason: reason || "",
          stakeholderVisible: true,
          syncStatus: "ACTIVE",
          lastUpdated: serverTimestamp(),
          workflowSyncedAt: serverTimestamp(),
        });
      }
    } catch (e) {
      console.error("[syncTaskDecisionToLinkedProject] error", e);
      toast.error("Task decision saved, but Project Dashboard sync failed.");
    }
  };

  const handleApprove = async (task) => {
    if (task.adminStatus === "approved") return;
    try {
      await updateDoc(doc(db, "wf_project_tasks", task.id), {
        adminStatus: "approved",
        adminReason: "",
        adminDecidedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await syncTaskDecisionToLinkedProject(task, "approved", "");

      toast.success("Task approved & synced to Projects Dashboard");
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? {
                ...t,
                adminStatus: "approved",
                adminReason: "",
                updatedAt: Timestamp.now(),
              }
            : t,
        ),
      );
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Approve failed");
    }
  };

  const handleReject = async (task) => {
    const reason = window.prompt(
      "Reject reason likhiye (ye vendor ko mail me jayega):",
      task.adminReason || "",
    );
    if (!reason) return;
    try {
      await updateDoc(doc(db, "wf_project_tasks", task.id), {
        adminStatus: "rejected",
        adminReason: reason,
        adminDecidedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await syncTaskDecisionToLinkedProject(task, "rejected", reason);

      toast.success("Task rejected & synced to Projects Dashboard");
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? {
                ...t,
                adminStatus: "rejected",
                adminReason: reason,
                updatedAt: Timestamp.now(),
              }
            : t,
        ),
      );
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Reject failed");
    }
  };

  const selectedProject =
    selectedProjectId && filteredViews.find((p) => p.id === selectedProjectId);
  const selectedTasks =
    selectedProject && tasks.filter((t) => t.projectId === selectedProject.id);

  return (
    <div className="space-y-4">
      {/* header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-emerald-800">
            Projects Dashboard (Internal – Admin Only)
          </div>
          <div className="text-[11px] text-gray-500">
            Filter projects by district / stage, see progress and open
            stage-wise details with Admin decision.
          </div>
        </div>
        <button
          onClick={load}
          className="px-3 py-2 rounded-lg border text-xs hover:bg-emerald-50"
        >
          Reload
        </button>
      </div>

      {/* cards */}
      <div className="grid md:grid-cols-5 gap-3">
        <StatCard
          label="Total Projects"
          value={totals.total}
          color="dark"
          icon="📁"
        />
        <StatCard
          label="Not Started"
          value={totals.notStarted}
          color="pink"
          icon="⏸"
        />
        <StatCard
          label="In Progress"
          value={totals.inProgress}
          color="blue"
          icon="📈"
        />
        <StatCard
          label="Near Completion"
          value={totals.nearCompletion}
          color="purple"
          icon="🔄"
        />
        <StatCard
          label="Completed"
          value={totals.completed}
          color="orange"
          icon="✅"
        />
      </div>

      {/* filters */}
      <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
        <div>
          <Label>District</Label>
          <select
            className="mt-1 w-full px-3 py-2 border rounded-xl text-sm bg-white"
            value={districtFilter}
            onChange={(e) => setDistrictFilter(e.target.value)}
          >
            {districtOptions.map((d) => (
              <option key={d} value={d}>
                {d === "all" ? "All" : d}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label>Stage</Label>
          <select
            className="mt-1 w-full px-3 py-2 border rounded-xl text-sm bg-white"
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
          >
            {stageOptions.map((s) => (
              <option key={s} value={s}>
                {s === "all" ? "All" : s}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-1 lg:col-span-2">
          <Label>Search</Label>
          <input
            className="mt-1 w-full px-3 py-2 border rounded-xl text-sm bg-white"
            placeholder="Search by PACS / project / village / district / ID…"
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
          />
        </div>
      </div>

      {/* summaries */}
      <div className="grid md:grid-cols-3 gap-3 mt-4">
        <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
          <div className="text-sm font-semibold text-slate-800 mb-1">
            Projects by District
          </div>
          <div className="text-[11px] text-slate-500 mb-3">
            Auto summary from current filtered projects
          </div>
          {districtSummary.length === 0 ? (
            <div className="text-xs text-slate-500">
              No data for current filters.
            </div>
          ) : (
            <div className="space-y-2 text-xs">
              {districtSummary.map(([district, count]) => (
                <div key={district}>
                  <div className="flex justify-between">
                    <span>{district}</span>
                    <span className="font-semibold">{count}</span>
                  </div>
                  <ProgressBar
                    value={(count / (filteredViews.length || 1)) * 100}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
          <div className="text-sm font-semibold text-slate-800 mb-1">
            Progress Distribution
          </div>
          <div className="text-[11px] text-slate-500 mb-3">
            Buckets based on project progress%
          </div>
          <div className="space-y-2 text-xs">
            {Object.entries(progressBuckets).map(([bucket, count]) => (
              <div key={bucket}>
                <div className="flex justify-between">
                  <span>{bucket}</span>
                  <span className="font-semibold">{count}</span>
                </div>
                <ProgressBar
                  value={(count / (filteredViews.length || 1)) * 100}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
          <div className="text-sm font-semibold text-slate-800 mb-1">
            Stage Summary
          </div>
          <div className="text-[11px] text-slate-500 mb-3">
            Based on current filtered projects
          </div>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-slate-500">
                <th className="text-left py-1">Stage</th>
                <th className="text-right py-1">Count</th>
                <th className="text-right py-1">%</th>
              </tr>
            </thead>
            <tbody>
              {[
                "Not Started",
                "In Progress",
                "Near Completion",
                "Completed",
              ].map((stage) => {
                const count = filteredViews.filter(
                  (p) => p.stage === stage,
                ).length;
                const pct =
                  filteredViews.length === 0
                    ? 0
                    : ((count / filteredViews.length) * 100).toFixed(1);
                return (
                  <tr key={stage}>
                    <td className="py-0.5">{stage}</td>
                    <td className="py-0.5 text-right">{count}</td>
                    <td className="py-0.5 text-right">{pct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Project register table */}
      <div className="mt-5">
        <div className="text-sm font-semibold text-slate-800 mb-1">
          Project Register (Filtered View)
        </div>
        <div className="text-[11px] text-slate-500 mb-2">
          {filteredViews.length} project(s) found
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-x-auto">
          {loading ? (
            <div className="px-4 py-6 text-xs text-slate-500">
              Loading projects…
            </div>
          ) : filteredViews.length === 0 ? (
            <div className="px-4 py-6 text-xs text-slate-500">
              No projects for selected filters.
            </div>
          ) : (
            <table className="min-w-full text-xs">
              <thead className="bg-slate-100 border-b border-slate-200">
                <tr className="text-slate-600">
                  <th className="px-3 py-2 text-left w-10">#</th>
                  <th className="px-3 py-2 text-left">PACS / Project</th>
                  <th className="px-3 py-2 text-left w-44">District</th>
                  <th className="px-3 py-2 text-left w-40">Stage</th>
                  <th className="px-3 py-2 text-left w-48">Progress</th>
                  <th className="px-3 py-2 text-left w-28">Last Updated</th>
                  <th className="px-3 py-2 text-left w-20">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredViews.map((p, idx) => (
                  <tr
                    key={p.id}
                    className={cx(
                      "border-b border-slate-100 hover:bg-slate-50",
                      idx % 2 === 0 ? "bg-white" : "bg-slate-50/40",
                    )}
                  >
                    <td className="px-3 py-2 align-top text-slate-700">
                      {idx + 1}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="font-semibold text-slate-900 text-xs">
                        {p.pacsName || "—"}
                      </div>
                      <div className="text-[11px] text-slate-600 truncate max-w-xs">
                        {p.title || "—"}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        ID: {p.id}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="text-[11px] text-slate-800">
                        {p.district || "—"}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {p.taluka || "—"}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {p.locationVillage || "—"}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <Badge
                        color={
                          p.stage === "Completed"
                            ? "emerald"
                            : p.stage === "Near Completion"
                              ? "blue"
                              : p.stage === "In Progress"
                                ? "blue"
                                : "gray"
                        }
                      >
                        {p.stage}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <ProgressBar value={p.progress} />
                        </div>
                        <div className="w-10 text-right text-[11px] font-semibold text-slate-700">
                          {p.progress}%
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top text-[11px] text-slate-700">
                      {p.lastUpdated?.seconds
                        ? dayjs(p.lastUpdated.toDate()).format("DD/MM/YYYY")
                        : "—"}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedProjectId(
                            selectedProjectId === p.id ? null : p.id,
                          )
                        }
                        className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 underline"
                      >
                        {selectedProjectId === p.id ? "Hide »" : "View »"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* detail for selected project */}
      {selectedProject && selectedTasks && (
        <div className="mt-5 border border-emerald-200 rounded-2xl bg-white overflow-x-auto">
          <div className="px-4 py-3 border-b border-emerald-200 bg-emerald-50 flex justify-between items-center">
            <div>
              <div className="font-semibold text-emerald-900 text-sm">
                {selectedProject.title || "Untitled Project"}
              </div>
              <div className="text-[11px] text-gray-700">
                {selectedProject.pacsName || "— PACS —"}
              </div>
              <div className="text-[11px] text-gray-600">
                {selectedProject.locationVillage || "—"},{" "}
                {selectedProject.taluka || "—"},{" "}
                {selectedProject.district || "—"},{" "}
                {selectedProject.state || "—"}
              </div>
            </div>
            <button
              className="text-[11px] text-emerald-700 underline"
              onClick={() => setSelectedProjectId(null)}
            >
              Close
            </button>
          </div>

          <table
            className="min-w-full text-xs"
            style={{ borderCollapse: "collapse" }}
          >
            <thead className="bg-emerald-50">
              <tr>
                <th className="px-2 py-2 border border-black text-left">
                  Type
                </th>
                <th className="px-2 py-2 border border-black text-left">
                  Vendor
                </th>
                <th className="px-2 py-2 border border-black text-left">
                  Status
                </th>
                <th className="px-2 py-2 border border-black text-left">
                  Files
                </th>
                <th className="px-2 py-2 border border-black text-left">
                  Planned / Timeline
                </th>
                <th className="px-2 py-2 border border-black text-left">
                  Admin Decision
                </th>
              </tr>
            </thead>
            <tbody>
              {selectedTasks.map((t) => {
                const vendor = vendors.find((v) => v.id === t.vendorId);
                const files = Array.isArray(t.files) ? t.files : [];
                return (
                  <tr
                    key={t.id}
                    className="border border-black hover:bg-emerald-50"
                  >
                    <td className="px-2 py-1 border border-black align-top">
                      <Badge color="blue">{labelType(t.type)}</Badge>
                    </td>
                    <td className="px-2 py-1 border border-black align-top">
                      <div className="text-[11px]">{vendor?.name || "—"}</div>
                      <div className="text-[11px] text-gray-500">
                        {vendor?.firmName || ""}
                      </div>
                    </td>
                    <td className="px-2 py-1 border border-black align-top">
                      <Badge color={statusColor(t.status || "ASSIGNED")}>
                        {t.status || "ASSIGNED"}
                      </Badge>
                    </td>
                    <td className="px-2 py-1 border border-black align-top">
                      {files.length ? (
                        <div className="text-[11px] space-y-1">
                          <div>{files.length} file(s)</div>
                          {files.slice(0, 3).map((f, idx) => (
                            <div key={idx}>
                              <a
                                href={f.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-emerald-700 underline"
                              >
                                {f.name || `File ${idx + 1}`}
                              </a>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[11px] text-gray-400">
                          No files
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1 border border-black align-top">
                      <div className="text-[11px]">
                        <b>Assigned:</b>{" "}
                        {t.assignedAt?.toDate
                          ? t.assignedAt.toDate().toLocaleString()
                          : "—"}
                      </div>
                      <div className="text-[11px]">
                        <b>Planned:</b> {t.plannedDateTime || "—"}
                      </div>
                      <div className="text-[11px]">
                        <b>Completed:</b>{" "}
                        {t.completedAt?.toDate
                          ? t.completedAt.toDate().toLocaleString()
                          : "—"}
                      </div>
                    </td>
                    <td className="px-2 py-1 border border-black align-top">
                      {t.status !== "COMPLETED" ? (
                        <span className="text-[11px] text-gray-500">
                          Wait for completion
                        </span>
                      ) : t.adminStatus === "approved" ? (
                        <Badge color="green">Approved</Badge>
                      ) : t.adminStatus === "rejected" ? (
                        <div className="text-[11px] space-y-1">
                          <Badge color="red">Rejected</Badge>
                          {t.adminReason && (
                            <div className="text-[11px] text-gray-600">
                              Reason: {t.adminReason}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => handleApprove(t)}
                            className="px-2 py-1 rounded bg-emerald-600 text-white text-[11px] hover:bg-emerald-700"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReject(t)}
                            className="px-2 py-1 rounded bg-red-600 text-white text-[11px] hover:bg-red-700"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
